import React, { useEffect, useState, useContext } from 'react'
import { useHistory } from 'react-router-dom'
import _ from 'lodash'
import Moment from 'react-moment'
import moment from 'moment'
import { motion } from 'framer-motion'
import TagManager from 'react-gtm-module'
import { useSelector } from 'react-redux'

import ScreenLoading from 'components/ScreenLoading'
import PopUpMessage from 'components/PopUpMessage'

import useGetAuthUrl from 'containers/services/useGetAuthUrl'
import useGetConnectedAccounts from 'containers/services/useGetConnectedAccounts'
import useDeleteServiceAccount from 'containers/services/deleteServiceAccount'
import useGetServices from 'containers/services/useGetServices'
import OrganizationsContext from 'containers/organizations/organizationsContext'
import { selectToken } from 'containers/authentication/authenticationSlice'
import useShopifyStoreSwitch from 'containers/shopify/useShopifyStoreSwitch'
import useShopifyReconnected from 'containers/shopify/useShopifyReconnected'
import api from 'containers/api'

import './Sources.scss'
import Feature from 'components/Feature'
import UpgradeButton from 'components/UpgradeButton'
import useGetCustomTables from 'containers/customTable/useGetCustomTables'
import useListManualExpenses from 'containers/expenses/useListManualExpenses'
import { FetchStatus } from 'containers/dataLoader'
import Modal from 'components/Modal'
import UploadZone from 'components/UploadZone'
import { getCSVColumns } from 'components/FileUpload/utils'
import useGetCustomTableColumns from 'containers/customTable/useGetCustomTableColumns'
import useUploadCustomTable from 'containers/customTable/useUploadCustomTable'
import useDeleteCustomTable from 'containers/customTable/useDeleteCustomTable'
import ProgressBar from 'components/ProgressBar'
import useConfirmation from 'containers/confirmation/useConfirmation'

const SyncStatus = ({ syncStatus, syncError }) => {
  const SyncDetails = () => {
    if (syncStatus === 'failed') {
      return <span className="status-info">{syncError}</span>
    }

    if (syncStatus === 'pending') {
      return <span className="status-info">Pending</span>
    }

    if (syncStatus === 'running') {
      return (
        <>
          <div className="spinner-grow text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <span className="status-info">Running</span>
        </>
      )
    }

    return <></>
  }

  return (
    <td className="status grid grid-column-3">
      <div className={`status-circle grid-column-1 ${syncStatus}`}></div>
      <SyncDetails />
    </td>
  )
}

const Account = ({
  isSourceOpen,
  account,
  deleteServiceAccount,
  removeServiceAccount,
  handleReconnect,
}) => {
  const [actionsOpen, setActionsOpen] = useState(false)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)

  const { currentOrganization } = useContext(OrganizationsContext)

  if (!isSourceOpen) {
    return <></>
  }

  const handleCloseAll = () => {
    setActionsOpen(false)
    setConfirmDeleteOpen(false)
    document.removeEventListener('click', handleCloseAll)
  }

  const handleClickActions = (e) => {
    e.stopPropagation()
    if (!actionsOpen && !confirmDeleteOpen) {
      document.addEventListener('click', handleCloseAll)
    }
    if (confirmDeleteOpen) {
      setConfirmDeleteOpen(false)
      return
    }

    setActionsOpen(!actionsOpen)
  }

  const handleClickDelete = () => {
    TagManager.dataLayer({
      dataLayer: { event: 'clicked_delete_source_account_button', is_custom_track: true },
    })
    setActionsOpen(false)
    setConfirmDeleteOpen(true)
    document.addEventListener('click', handleCloseAll)
  }

  const handleConfirmDelete = async () => {
    const deletedAccount = await deleteServiceAccount(
      account.uuid,
      currentOrganization.id
    )

    if (deletedAccount) {
      TagManager.dataLayer({ dataLayer: { event: 'source_account_deleted', is_custom_track: true } })
      removeServiceAccount(deletedAccount.uuid)
    }
  }

  return (
    <>
      <tr className="account grid">
        <td className="integration grid-column-5">
          <div className="information">
            <span className="name">{account.name}</span>
            <span className="uuid">{account.id_label}</span>
          </div>
        </td>
        <SyncStatus
          syncStatus={account.sync_status}
          syncError={account.sync_error}
        />
        <td className="date grid-column-3">
          <span className="created">
            Created: <Moment format="DD/MM/YYYY">{account.connected_at}</Moment>
          </span>
        </td>
        <td className="actions grid-column-1">
          <div
            onClick={handleClickActions}
            className={`actions-drop-down ${actionsOpen ? 'open' : ''}`}
          >
            Actions
            <span className="material-icons">keyboard_arrow_down</span>
            {actionsOpen && !currentOrganization.id && <ScreenLoading />}
            {actionsOpen && currentOrganization.id && (
              <ul className="actions-drop-down-options">
                <li onClick={() => handleReconnect()} className="reconnect">
                  <span className="material-icons-outlined">link</span>
                  Reconnect
                </li>
                <li onClick={handleClickDelete} className="delete">
                  <span className="material-icons-outlined">delete</span>
                  Delete
                </li>
              </ul>
            )}
            {confirmDeleteOpen && (
              <div className="delete-confirmation">
                <span className="message">Are you sure?</span>
                <span onClick={handleConfirmDelete} className="delete">
                  Delete
                </span>
              </div>
            )}
          </div>
        </td>
      </tr>
    </>
  )
}

const AccountsHeader = ({ isSourceOpen }) => {
  if (!isSourceOpen) {
    return <></>
  }

  return (
    <tr className="accounts-header grid">
      <th className="integration grid-column-5">Source Account</th>
      <th className="status grid-column-3">Status</th>
      <th className="date grid-column-3">Date</th>
      <th className="actions grid-column-1"></th>
    </tr>
  )
}

const Service = ({
  service,
  deleteServiceAccount,
  removeServiceAccount,
  handleReconnect,
  handleReconnectShopify,
}) => {
  const [isSourceOpen, setIsSourceOpen] = useState(false)
  const { currentOrganization } = useContext(OrganizationsContext)
  const history = useHistory()

  const totalSyncStatus = () => {
    const totalFailed = _.filter(
      service.accounts,
      (acc) => acc.sync_status === 'failed'
    ).length

    if (totalFailed > 0) {
      return (
        <SyncStatus
          syncStatus="failed"
          syncError={`${totalFailed} Sync Problem${totalFailed > 1 ? 's' : ''}`}
        />
      )
    }

    const totalPending = _.filter(
      service.accounts,
      (acc) => acc.sync_status === 'pending'
    ).length

    if (totalPending > 0) {
      return <SyncStatus syncStatus="pending" />
    }

    const totalRunning = _.filter(
      service.accounts,
      (acc) => acc.sync_status === 'running'
    ).length

    if (totalRunning > 0) {
      return <SyncStatus syncStatus="running" />
    }

    return <SyncStatus syncStatus="finished" />
  }

  const _getEarliestSyncTime = () => {
    let moments = []
    service.accounts.forEach((acc) => {
      if (acc.last_synced_at) {
        moments.push(moment(acc.last_synced_at))
      }
    })
    const minDate = moment.min(moments)

    if (moments.length === 0) {
      return null
    }

    return minDate
  }

  const getEarliestCreatedTime = () => {
    let moments = []
    service.accounts.forEach((acc) => {
      if (acc.connected_at) {
        moments.push(moment(acc.connected_at))
      }
    })
    const minDate = moment.min(moments)

    if (moments.length === 0) {
      return null
    }

    return minDate
  }

  const handleReconnectService = (service, account) => {
    if (service.details.id === 'google_analytics_4_events') {
      history.push(
        `/organization/${currentOrganization.slug}/sources/setup/google_analytics_4_events/${service.details.uuid}`
      )
      return
    }
    if (service.details.id === 'shopify') {
      handleReconnectShopify(account)
      return
    }
    // API-key integrations (moosend, klaviyo, apple_ads) have no OAuth authorization URL, so
    // /services/{uuid}/oauth/start raised AttributeError -> 500 and this button did nothing.
    // Send the user to the service's own Configure page instead; re-entering the key there
    // updates the EXISTING account in place (reuse_existing_account_identifier, #1755) rather
    // than creating a duplicate. Driven by the backend flag so a new API-key integration works
    // without touching this list.
    if (service.details.requires_oauth === false) {
      history.push(
        `/organization/${currentOrganization.slug}/sources/setup/${service.details.id}/${service.details.uuid}`
      )
      return
    }
    handleReconnect(service.details.uuid)
  }


  return (
    <tbody key={service.details.id}>
      <tr
        className={`service grid ${isSourceOpen ? 'open' : ''}`}
        onClick={() => setIsSourceOpen(!isSourceOpen)}
      >
        <td className="integration grid-column-5">
          <img src={service.details.logo} width="35" alt="service_icon" />
          <div className="information">
            <span className="name">{service.details.name}</span>
            <span className="connections">
              {service.accounts.length} connection
              {service.accounts.length > 1 ? 's' : ''}
            </span>
          </div>
        </td>
        {totalSyncStatus()}
        <td className="date grid-column-3">
          <span className="created">
            Created:{' '}
            <Moment format="DD/MM/YYYY">{getEarliestCreatedTime()}</Moment>
          </span>
        </td>
        <td className="actions grid-column-1">
          <span className="material-icons">keyboard_arrow_down</span>
        </td>
      </tr>
      <AccountsHeader isSourceOpen={isSourceOpen} />
      {service.accounts.map((account) => (
        <Account
          key={account.uuid}
          isSourceOpen={isSourceOpen}
          account={account}
          deleteServiceAccount={deleteServiceAccount}
          removeServiceAccount={removeServiceAccount}
          handleReconnect={() => handleReconnectService(service, account)}
        />
      ))}
    </tbody>
  )
}

const IntegrationsCategory = ({
  category,
  deleteServiceAccount,
  removeServiceAccount,
  handleReconnect,
  handleReconnectShopify,
}) => {
  return (
    <div className="integrations-category">
      <span className="title">{category.name}</span>
      <span className="subtitle">
        {category.services.length} integration
        {category.services.length > 1 ? 's' : ''}
      </span>

      <table>
        <thead>
          <tr className="grid">
            <th className="integration grid-column-5">Integrations</th>
            <th className="status grid-column-3">Status</th>
            <th className="date grid-column-3">Date</th>
            <th className="actions grid-column-1"></th>
          </tr>
        </thead>
        {category.services.map((service) => (
          <Service
            key={service.details.id}
            service={service}
            deleteServiceAccount={deleteServiceAccount}
            removeServiceAccount={removeServiceAccount}
            handleReconnect={handleReconnect}
            handleReconnectShopify={handleReconnectShopify}
          />
        ))}
      </table>
    </div>
  )
}

const NoSourcesConnected = ({ handleAddSources }) => {
  return (
    <div className="no-sources">
      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="box">
        <h1>Connect sources</h1>
        <div className="subtitle-text">
          No integrations connected. Add an account to get started with Maya
          Insights.
        </div>
        <Feature
          feature="can_add_accounts"
          switchComponent={
            <UpgradeButton style={{ marginTop: 20 }}>
              Upgrade plan for more accounts
            </UpgradeButton>
          }
        >
          <button onClick={handleAddSources} className="submit-button">
            <span className="enlarge">+</span>Add source
          </button>
        </Feature>
      </motion.div>
    </div>
  )
}

// localStorage throws on access in blocked-storage contexts (strict private mode,
// third-party iframes such as the Shopify admin). A throw during render would
// white-screen the page, so the popup flags degrade to "no popup" instead.
const safeStorageGet = (key) => {
  try {
    return localStorage.getItem(key)
  } catch (e) {
    return null
  }
}

const safeStorageRemove = (key) => {
  try {
    localStorage.removeItem(key)
  } catch (e) {
    // nothing to clean up if storage is unavailable
  }
}

// `embedded`: rendered as the Integrations tab of the Data & Insights flow — the shell
// already provides the screen title and tab label, so the page's own header/nav hide.
const Sources = ({ embedded = false }) => {
  let history = useHistory()
  const { services, servicesError, getServices, loadingServices } =
    useGetServices()
  const {
    serviceAccounts,
    loadingServicesAccounts,
    serviceAccountsError,
    getServiceAccounts,
    removeServiceAccount,
  } = useGetConnectedAccounts()

  const { deleteServiceAccount } = useDeleteServiceAccount()
  const { getAuthUrl } = useGetAuthUrl()
  const { currentOrganization } = useContext(OrganizationsContext)
  const [customTables, getCustomTables, fetchingCustomTables] =
    useGetCustomTables(currentOrganization)
  const [manualExpenses, fetchManualExpenses, fetchingManualExpenses] =
    useListManualExpenses(currentOrganization.id ?? null)
  const [, deleteTable] = useDeleteCustomTable(currentOrganization)
  const { confirm } = useConfirmation()

  useEffect(() => {
    getServices()
    getCustomTables()
  }, [getServices])

  useEffect(() => {
    if (currentOrganization.id) {
      getServiceAccounts(currentOrganization.id)
      getCustomTables()
      fetchManualExpenses()
    }
    // eslint-disable-next-line
  }, [currentOrganization.id])

  // The Manual Expenses card shows only when the org has recorded expenses;
  // entry point for orgs without any is the Add Source page.
  const hasManualExpenses = manualExpenses?.length > 0

  const token = useSelector(selectToken)

  // Pending Shopify store switch (from the OAuth-connect redirect's ?shopify_switch=… params):
  // confirm modal + apply, with all the integrity/one-shot handling encapsulated in the hook.
  useShopifyStoreSwitch(getServiceAccounts)

  // Entitled reconnect acknowledgement (from the OAuth redirect's ?shopify_reconnected=…
  // params): refresh + info dialog, no billing action (#1676).
  useShopifyReconnected(getServiceAccounts)

  const [reconnectError, setReconnectError] = useState('')

  if (servicesError) {
    return <>{servicesError}</>
  }

  if (serviceAccountsError) {
    return <>{serviceAccountsError}</>
  }

  if (!currentOrganization.id || loadingServices || loadingServicesAccounts) {
    return <ScreenLoading />
  }

  // The expenses fetch must settle before choosing between the empty state
  // and the Manual Expenses card — its initial [] would flash
  // "No sources connected" for an expenses-only org.
  if (
    serviceAccounts.length === 0 &&
    customTables?.length === 0 &&
    fetchingManualExpenses !== FetchStatus.Fulfilled &&
    fetchingManualExpenses !== FetchStatus.Rejected
  ) {
    return <ScreenLoading />
  }

  const getOrganizedCategories = () => {
    let organizedServices = []
    let organizedCategories = []

    serviceAccounts.forEach((account) => {
      const service = _.find(services, ['id', account.service])

      const serviceIndex = _.findIndex(
        organizedServices,
        (srv) => srv.details.id === service.id
      )
      if (serviceIndex !== -1) {
        organizedServices[serviceIndex].accounts.push(account)
      } else {
        organizedServices.push({
          details: service,
          accounts: [account],
        })
      }
    })

    organizedServices.forEach((service) => {
      const category = service.details.category
      const categoryIndex = _.findIndex(
        organizedCategories,
        (ctg) => ctg.name === category
      )

      if (categoryIndex !== -1) {
        organizedCategories[categoryIndex].services.push(service)
      } else {
        organizedCategories.push({
          name: category,
          services: [service],
        })
      }
    })

    return organizedCategories
  }

  const handleAddSources = () => {
    TagManager.dataLayer({ dataLayer: { event: 'clicked_add_sources', is_custom_track: true } })
    history.push(`/organization/${currentOrganization.slug}/sources/add`)
  }

  const handleReconnect = async (serviceUuid) => {
    const url = await getAuthUrl(serviceUuid, currentOrganization.id)
    if (url) {
      TagManager.dataLayer({
        dataLayer: { event: 'clicked_reconnect_source_account', is_custom_track: true },
      })
      window.location.replace(url)
    }
  }

  const handleReconnectShopify = async (account) => {
    // Shopify OAuth needs the shop domain up front, which the generic
    // /services/{uuid}/oauth/start endpoint can't supply — it raised
    // "shop_domain is required" and the reconnect button did nothing. Route
    // through the Shopify-specific start using the account's known shop.
    // id_label is the shop name (always set by both Shopify account-creation
    // paths); `name` is the store DISPLAY name, which fails the backend's shop
    // validation — never fall back to it.
    const shop = account?.id_label
    if (!shop) {
      setReconnectError('Could not determine the Shopify store for this account.')
      return
    }
    try {
      const { authorization_url } = await api.startShopifyOAuth({
        shop,
        organizationId: currentOrganization.id,
        token,
      })
      if (authorization_url) {
        TagManager.dataLayer({
          dataLayer: { event: 'clicked_reconnect_source_account', is_custom_track: true },
        })
        window.location.replace(authorization_url)
      }
    } catch (e) {
      // A silent failure here reproduces the original "button does nothing" bug —
      // always surface it.
      console.error('Failed to start Shopify reconnect:', e)
      setReconnectError('Could not start the Shopify reconnect. Please try again or contact support.')
    }
  }

  const handleDeleteTable = async (tableName) => {
    if (await confirm('Are you sure you want to delete this table?')) {
      await deleteTable(tableName)
      getCustomTables()
    }
  }

  return (
    <div className="sources">
      {safeStorageGet('checkoutCompleted') && (
        <PopUpMessage
          onClose={() => safeStorageRemove('checkoutCompleted')}
          message="Payment completed successfully."
          time={3000}
        />
      )}
      {reconnectError && (
        <PopUpMessage
          onClose={() => setReconnectError('')}
          message={reconnectError}
          time={6000}
        />
      )}
      {serviceAccounts.length === 0 &&
      customTables?.length === 0 &&
      !hasManualExpenses ? (
        <>
          <NoSourcesConnected handleAddSources={handleAddSources} />
          {/* <div className="video-walkthrough">
            <h3>Getting Started with Sources</h3>
            <p className="video-description">
              Watch this quick walkthrough to learn how to connect your data sources and get the most out of Maya Insights.
            </p>
            <div className="video-container">
              <iframe
                width="100%"
                height="315"
                src="https://www.youtube.com/embed/dQw4w9WgXcQ"
                title="Maya Insights Sources Walkthrough"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div> */}
        </>
      ) : (
        <>
          <div className="head">
            <div className="page-name">
              {!embedded && <h2>Sources</h2>}
              <span className="subtitle-text">
                Connect the tools you already use — Shopify, Google, Facebook
                and more. Maya brings their data together and turns it into
                insights.{' '}
              </span>
            </div>
            <Feature
              feature="can_add_accounts"
              switchComponent={
                <UpgradeButton style={{ marginLeft: 'auto' }}>
                  Upgrade plan for more accounts
                </UpgradeButton>
              }
            >
              <button onClick={handleAddSources} className="submit-button">
                <span className="enlarge">+</span>
                <span>Add source</span>
              </button>
            </Feature>
          </div>
          {!embedded && (
            <nav>
              <ul>
                <li className="selected">Integrations</li>
              </ul>
            </nav>
          )}
          <div className="content">
            {getOrganizedCategories().map((category) => (
              <IntegrationsCategory
                key={category.name}
                category={category}
                deleteServiceAccount={deleteServiceAccount}
                removeServiceAccount={removeServiceAccount}
                handleReconnect={handleReconnect}
                handleReconnectShopify={handleReconnectShopify}
              />
            ))}
            {fetchingCustomTables === FetchStatus.Pending && <ScreenLoading />}
            {(customTables?.length > 0 || hasManualExpenses) && (
              <ManualUploadsCategory
                customTables={customTables || []}
                hasManualExpenses={hasManualExpenses}
                currentOrganization={currentOrganization}
                handleDeleteTable={handleDeleteTable}
              />
            )}
          </div>
          {safeStorageGet('serviceConnected') && (
            <PopUpMessage
              onClose={() => safeStorageRemove('serviceConnected')}
              message={`${safeStorageGet('serviceConnected')} connected succesfully.`}
              time={3000}
            />
          )}
        </>
      )}
    </div>
  )
}

// Manual Uploads groups all user-entered data in one section, mirroring the
// Add Source category of the same name: the org's custom tables plus, when
// expenses exist (see hasManualExpenses above), the Manual Expenses row.
// The Add Source page carries the always-visible entry point.
const ManualUploadsCategory = ({
  customTables,
  hasManualExpenses,
  currentOrganization,
  handleDeleteTable,
}) => {
  const count = customTables.length + (hasManualExpenses ? 1 : 0)
  return (
    <div className="integrations-category">
      <span className="title">Manual Uploads</span>
      <span className="subtitle">
        {count} source{count > 1 ? 's' : ''}
      </span>
      <table>
        <thead>
          <tr className="grid">
            <th className="integration grid-column-5">Source</th>
            <th className="status grid-column-3">Status</th>
            <th className="uuid grid-column-3">UUID</th>
            <th className="actions grid-column-1"></th>
          </tr>
        </thead>
        {customTables.map((table) => (
          <CustomTableIntegration
            key={table.uuid}
            table={table}
            currentOrganization={currentOrganization}
            handleDeleteTable={handleDeleteTable}
          />
        ))}
        {hasManualExpenses && (
          <ManualExpensesIntegration
            currentOrganization={currentOrganization}
          />
        )}
      </table>
    </div>
  )
}

// Row inside ManualUploadsCategory; expenses are managed on the /expenses pages.
const ManualExpensesIntegration = ({ currentOrganization }) => {
  const history = useHistory()
  const handleManage = () => {
    history.push(`/organization/${currentOrganization.slug}/expenses`)
  }

  return (
    <tbody>
      <tr className="service grid">
            <td className="integration grid-column-5">
              {/* Same tile as the Add Source card (styles in AddSource.scss) */}
              <div className="manual-tile sm green">
                <span className="material-icons-outlined">payments</span>
              </div>
              <div className="information">
                <span className="name">Manual Expenses</span>
                <span className="connections">
                  Expenses you record for CPA metrics and reports
                </span>
              </div>
            </td>
            <td className="status grid-column-3"></td>
            <td className="uuid grid-column-3"></td>
            <td className="actions grid-column-1">
              <div onClick={handleManage} className="actions-drop-down open">
                Manage
                <span className="material-icons">chevron_right</span>
              </div>
            </td>
          </tr>
    </tbody>
  )
}

const CustomTableIntegration = ({
  table,
  currentOrganization,
  handleDeleteTable,
}) => {
  const [isActionsOpen, setIsActionsOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [csvFile, setCSVFile] = useState(null)
  const [csvColumns, setCsvColumns] = useState([])
  const [uploadStatus, setUploadStatus] = useState(null)
  const [clearFiles, setClearFiles] = useState(false)

  const [fileTypeError, setFileTypeError] = useState(false)
  const [invalidColumnsError, setInvalidColumnsError] = useState(false)
  const [uploadError, setUploadError] = useState(null)

  const [customTableColumns, getCustomTableColumns] =
    useGetCustomTableColumns(currentOrganization)
  const [uploadResult, uploadFile, uploadingFile] =
    useUploadCustomTable(currentOrganization)

  const history = useHistory()
  const handleEditSchema = () => {
    history.push(
      `/organization/${currentOrganization.slug}/sources/setup/custom-table/${table.name}/edit`
    )
  }

  const checkColumnExists = (columnName) => {
    if (csvColumns?.length <= 0) return ''
    const columnExistsInCsv = csvColumns.includes(columnName)
    const columnExistsInCustomTable = customTableColumns[table.name]?.find(
      (column) => column.columnName === columnName
    )
    if (columnExistsInCsv && columnExistsInCustomTable) {
      return 'found'
    }
    if (columnExistsInCustomTable) {
      return 'missing'
    }
  }

  const handleUploadFile = async (e) => {
    const files = e.target.files

    if (files[0].type !== 'text/csv') {
      setFileTypeError(true)
      return
    } else if (fileTypeError) {
      setFileTypeError(false)
    }

    if (files) {
      const { columns: columnsOnFile } = await getCSVColumns(files[0])
      setCsvColumns(columnsOnFile)
      setCSVFile(files[0])
      await getCustomTableColumns(table.name)

      const columnsAreTheSame = customTableColumns[table.name].every(
        (column) =>
          columnsOnFile.find((csvColumn) => csvColumn === column.columnName) !==
          undefined
      )
      if (!columnsAreTheSame) {
        setInvalidColumnsError(true)
        return
      }

      const uploadStatus = await uploadFile(
        [],
        table.name,
        files[0],
        setUploadStatus
      )
      if (uploadStatus?.success === false) {
        setUploadError(uploadStatus.detail)
        if (!uploadError) setUploadError(uploadStatus.detail)
        return
      } else if (uploadError) {
        setUploadError('')
      }
    }
  }

  const handleClearFiles = () => {
    setClearFiles(true)
    setCSVFile(null)
    setCsvColumns([])
    setUploadStatus(null)
    setTimeout(() => {
      setClearFiles(false)
    }, 100)

    if (fileTypeError) setFileTypeError(false)
    if (invalidColumnsError) setInvalidColumnsError(false)
    if (uploadError) setUploadError(null)
  }

  const unslugify = (str) => {
    return str
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  useEffect(() => {
    if (isModalOpen && !customTableColumns) {
      getCustomTableColumns(table.name)
    }
  }, [isModalOpen])

  useEffect(() => {
    if (uploadResult?.success) {
      setIsModalOpen(false)
    }
  }, [uploadResult])

  // Row inside ManualUploadsCategory (which owns the wrapper and header)
  return (
    <tbody>
          <tr className="service grid">
            <td className="integration grid-column-5">
              {/* Same tile family as the Add Source manual cards (AddSource.scss) */}
              <div
                className={`manual-tile sm ${
                  table.name === 'manual_users' ? 'blue' : 'amber'
                }`}
              >
                <span className="material-icons-outlined">
                  {table.name === 'manual_users' ? 'group' : 'shopping_cart'}
                </span>
              </div>
              <div className="information">
                <span className="name">{unslugify(table.name)}</span>
                <span className="connections"></span>
              </div>
            </td>
            <td className="status grid-column-3"></td>
            <td className="uuid grid-column-3">
              <span className="created">{table.uuid}</span>
            </td>
            <td className="actions grid-column-1">
              <div
                onClick={() => setIsActionsOpen((prev) => !prev)}
                className={`actions-drop-down open`}
              >
                Actions
                <span className="material-icons">keyboard_arrow_down</span>
                {isActionsOpen && !currentOrganization.id && <ScreenLoading />}
                {isActionsOpen && currentOrganization.id && (
                  <ul className="actions-drop-down-options">
                    <li onClick={handleEditSchema} className="reconnect">
                      <span className="material-icons-outlined">link</span>
                      Edit Schema
                    </li>
                    <li
                      onClick={() => setIsModalOpen(true)}
                      className="reconnect"
                    >
                      <span className="material-icons-outlined">link</span>
                      Upload new file
                    </li>
                    <li
                      onClick={() => handleDeleteTable(table.name)}
                      className="delete"
                    >
                      <span className="material-icons-outlined">delete</span>
                      Delete
                    </li>
                  </ul>
                )}
              </div>
              {/* Overlay modal: fixed-position, unaffected by living in the cell */}
              <Modal onHide={() => setIsModalOpen(false)} show={isModalOpen}>
                <div className="upload-file">
                  <div className="upload-file__header">
                    <h2>Upload a CSV file</h2>
                    <p>
                      Replace the currently stored CSV file with one that has the same
                      schema.
                    </p>
                    <div className="upload-file__column-schema">
                      {customTableColumns &&
                        customTableColumns[table.name].map((column) => (
                          <span
                            className={`upload-file__header__column ${checkColumnExists(
                              column.columnName
                            )}`}
                          >
                            {column.columnName}
                          </span>
                        ))}
                    </div>
                  </div>
                  <div className="upload-file__body">
                    {!csvFile && (
                      <div className="upload-file__body__upload">
                        <UploadZone
                          fileType="csv"
                          uploadFile={handleUploadFile}
                          uploadingFile={uploadingFile === FetchStatus.Pending}
                          clear={clearFiles}
                        />
                      </div>
                    )}
                    {csvFile && (
                      <>
                        <h4>Files</h4>
                        <div className="uploaded-file" onClick={handleClearFiles}>
                          <div className="upload-file__body__upload__file__name">
                            {csvFile.name}
                          </div>
                          <button className="clear-btn">&times;</button>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="upload-file__footer">
                    {uploadStatus !== null && (
                      <ProgressBar
                        loadValue={uploadStatus}
                        showProgress
                        progressAlignment="center"
                        switchWhenDone
                      >
                        {uploadError && (
                          <div className="upload-file__body__upload__error error">
                            {uploadError}
                          </div>
                        )}
                      </ProgressBar>
                    )}
                    {fileTypeError && (
                      <div className="upload-file__body__upload__error error">
                        Please upload a CSV file
                      </div>
                    )}
                    {invalidColumnsError && (
                      <div className="upload-file__body__upload__error error">
                        Current schema and CSV columns do not match
                      </div>
                    )}
                  </div>
                </div>
              </Modal>
            </td>
          </tr>
    </tbody>
  )
}

export default React.memo(Sources)
